import type { DomainConfiguration } from '../ConfigurationMapper';
import type { HybridStorageBlock } from '../StorageBlock';
import type { RulesContext, StorageSide } from './context';
import { isSkuAvailable } from '../../constants/quarterConfig';

// Ported from useConfigurationLogic.jsx:
//   calculateRequiredStorageNodes (lines 886-931)
//   getAvailableEncodingSchemes   (lines 873-882)
//   Rule 0 (node sizing)          Base :317-339, Expansion :251-273
//   Rule 1 (encoding reconcile)   :341-367
//   Rule 2 (jbod EOL)             :371-373
//   Rule 3 (at-least-one storage) :376-379
//   Rule 6 (storage toggle)       :382-404
//   Rule 9  (hybrid HDD SKU reset) :424-453
//   Rule 10 (hybrid SSD SKU reset) :454-481
//   Rule 11 (afa SSD SKU reset)    :483-510

export const minNodesFor = (systemType: string): number => (systemType === 'Expansion' ? 1 : 3);

export function requiredStorageNodes(
  next: DomainConfiguration, side: StorageSide, productDb: any, minNodes: number,
): number {
  const b = next.system[side];
  if (!productDb) return b.nodeCount;
  const ssdPerVpod = productDb.ssdPerVpod;
  const isHybrid = side === 'hybrid';
  let n = b.nodeCount || minNodes;
  const min = b.minRawCapacity;
  if (min <= 0) return n;
  const raw = (count: number) => isHybrid
    ? count * ssdPerVpod * b.vpodSsdCapacity
      + count * (b as HybridStorageBlock).jbodSize * (b as HybridStorageBlock).hddCapacity
    : count * ssdPerVpod * b.vpodSsdCapacity;
  let total = raw(n);
  while (total > min && n > minNodes) { n -= 1; total = raw(n); }
  while (total < min) { n += 1; total = raw(n); }
  return Math.max(minNodes, n);
}

export function applyNodeSizing(
  prev: DomainConfiguration, next: DomainConfiguration, side: StorageSide, ctx: RulesContext,
): void {
  const p = prev.system[side], b = next.system[side];
  const capacityChanged = side === 'hybrid'
    ? p.minRawCapacity !== b.minRawCapacity
      || (p as HybridStorageBlock).jbodSize !== (b as HybridStorageBlock).jbodSize
      || (p as HybridStorageBlock).hddCapacity !== (b as HybridStorageBlock).hddCapacity
      || p.vpodSsdCapacity !== b.vpodSsdCapacity || p.enabled !== b.enabled
    : p.minRawCapacity !== b.minRawCapacity || p.vpodSsdCapacity !== b.vpodSsdCapacity
      || p.enabled !== b.enabled;
  if (next.system.sizeByNodeCount || !b.enabled || !capacityChanged) return;
  const n = requiredStorageNodes(next, side, ctx.productDb, minNodesFor(next.system.systemType));
  if (n !== b.nodeCount) b.nodeCount = n;
}

// The ONLY encoding table: utils/configUtilities re-exports it for the UI, and R1
// below enforces it. Index 0 is the auto-pick, so order matters: widest first.
//
// CONF-222: built from a rule instead of typed by hand (the hand-typed lists had gaps,
// e.g. 7 nodes skipped 11+2 and 4+2). Each N+2 scheme has a minimum node count, the
// same shape WEKA/Ceph/Quobyte document, so adding nodes only ever ADDS options:
//   - at most MAX_MEMBERS_PER_NODE stripe members on one node (one-node tolerance
//     with +2 parity), so data D needs ceil((D+2)/2) nodes;
//   - never fewer than BASE_MIN_NODES, never wider than MAX_DATA, never narrower than MIN_DATA.
// CONF-207: '3+2' is the exception: offered from 5 nodes up, per side, and always last so
// it is never the auto-pick.
const PARITY = 2;
const MAX_MEMBERS_PER_NODE = 2;
const BASE_MIN_NODES = 3;
const MIN_DATA = 4;
const MAX_DATA = 16;
const THREE_PLUS_TWO_MIN_NODES = 5;

export function minNodesForScheme(dataBits: number): number {
  return Math.max(BASE_MIN_NODES, Math.ceil((dataBits + PARITY) / MAX_MEMBERS_PER_NODE));
}

export function availableEncodingSchemes(nodeCount: number): string[] {
  const schemes: string[] = [];
  for (let d = MAX_DATA; d >= MIN_DATA; d -= 1) {
    if (nodeCount >= minNodesForScheme(d)) schemes.push(`${d}+${PARITY}`);
  }
  if (schemes.length === 0) return ['4+2']; // below 3 nodes: legacy fallthrough
  if (nodeCount >= THREE_PLUS_TWO_MIN_NODES) schemes.push('3+2');
  return schemes;
}

export function applyEncodingReconcile(
  prev: DomainConfiguration, next: DomainConfiguration, side: StorageSide,
): void {
  const p = prev.system[side], b = next.system[side];
  if (p.nodeCount === b.nodeCount) return;
  if (side === 'afa' && !(b.nodeCount > 0)) return;
  // The user-selected flag lives in ViewState, not StorageBlock — this rule is cross-box.
  const flag = `${side}EncodingSchemeUserSelected` as const; // 'hybrid…' | 'afa…'
  const schemes = availableEncodingSchemes(b.nodeCount);
  if (!next.viewState[flag]) {
    b.encodingScheme = schemes[0]!;
  } else if (!schemes.includes(b.encodingScheme)) {
    b.encodingScheme = schemes[0]!;
    next.viewState[flag] = false;
  }
}

// Rule 2: JBOD constraints (legacy - EOL product rule).
// EOL product: 18TB drives were offered in a 108-bay JBOD; force to 24TB.
export function applyJbodEol(next: DomainConfiguration): void {
  const h = next.system.hybrid;
  if (h.jbodSize === 108 && h.hddCapacity === 18) {
    h.hddCapacity = 24;
  }
}

// Rule 3: at least one storage type must be enabled — default to hybrid if both are off.
export function applyAtLeastOneStorage(next: DomainConfiguration): void {
  const s = next.system;
  if (!s.hybrid.enabled && !s.afa.enabled) {
    s.hybrid.enabled = true;
    s.hybrid.nodeCount = Math.max(3, s.hybrid.nodeCount);
  }
}

// Rule 6: storage toggle rules — one side must always remain enabled for a Base System.
// The "other side is also disabled" branch below is unreachable when this runs after R3
// inside applyRulesFromDomain (R3 already guarantees one side is enabled), but it IS
// reachable when this helper is invoked standalone — ported verbatim from legacy.
export function applyToggleExclusion(
  prev: DomainConfiguration, next: DomainConfiguration, side: StorageSide,
): void {
  const other: StorageSide = side === 'hybrid' ? 'afa' : 'hybrid';
  const p = prev.system[side];
  const b = next.system[side];
  const o = next.system[other];
  if (p.enabled === b.enabled) return;
  if (!b.enabled) {
    if (!o.enabled) {
      o.enabled = true;
      o.nodeCount = 3;
    }
  } else {
    b.nodeCount = b.nodeCount || 3;
  }
}

// Rules 9/10/11 collapsed: reset a side's part-number selection when it becomes unavailable
// for the (possibly new) quarter. HDD (R9) also re-checks on jbodSize change; SSD (R10/R11)
// only re-checks on quarter change. SHARED rule — legacy runs this for BOTH systemTypes
// (applySharedBusinessRules, called by both the Expansion and BaseSystem branches).
//
// NOTE: legacy R9 does `pMap.hybridHdd[jbodSize].filter(...)` with NO existence guard — for
// a jbodSize absent from the vendor's hybridHdd map (e.g. DELL, keyed 60/120, given the
// AIC-default jbodSize 78) this throws. Reproduced verbatim below — do not add a guard.
export function applySkuReset(
  prev: DomainConfiguration, next: DomainConfiguration,
  side: StorageSide, drive: 'hdd' | 'ssd', ctx: RulesContext,
): void {
  const b = next.system[side];
  if (!b.enabled) return;
  const quarter = next.pricing.quarter;
  const quarterChanged = prev.pricing.quarter !== quarter;
  const pMap = ctx.capabilities.productMappings;

  if (drive === 'hdd') {
    const h = b as HybridStorageBlock;
    const ph = prev.system.hybrid as HybridStorageBlock;
    if (!(quarterChanged || ph.jbodSize !== h.jbodSize)) return;
    // No `|| []` fallback here — legacy indexes `pMap.hybridHdd[jbodSize]` with no existence
    // guard and throws when the key is absent (e.g. DELL + AIC-default jbodSize 78). Preserve
    // that crash exactly; do not add a guard (see storageRules.test.ts crash-parity test).
    const avail = pMap.hybridHdd[h.jbodSize]
      .filter((m: any) => isSkuAvailable(m.availableFrom, quarter));
    const currentAvailable = avail.some((m: any) => m.partNumber === h.hddPartNumber);
    if (!currentAvailable && avail.length > 0) {
      h.hddCapacity = avail[0].capacity;
      h.hddPartNumber = avail[0].partNumber;
    }
    return;
  }

  if (!quarterChanged) return;
  const list = side === 'hybrid' ? pMap.hybridSsd : pMap.afaSsd;
  const avail = (list || []).filter((m: any) => isSkuAvailable(m.availableFrom, quarter));
  const currentAvailable = avail.some((m: any) => m.partNumber === b.ssdPartNumber);
  if (!currentAvailable && avail.length > 0) {
    b.vpodSsdCapacity = avail[0].capacity;
    b.ssdPartNumber = avail[0].partNumber;
  }
}

// Rule 8: If enabling E2E, force compression to 1:1. SHARED rule — legacy runs this for BOTH
// systemTypes (applySharedBusinessRules :567-577).
// MORK NOTE (preserved from legacy): the system-level E2E/compression force is an artifact of
// an earlier implementation (before AFA/Hybrid had independent E2E options) and will be
// removed in the future.
export function applyE2ECompression(next: DomainConfiguration): void {
  const s = next.system;
  if (s.endToEndEncryption && s.compressionRatio !== 1.0) s.compressionRatio = 1.0;
  if (s.afa.endToEndEncryption && s.afa.compressionRatio !== 1.0 && s.afa.enabled) s.afa.compressionRatio = 1.0;
  if (s.hybrid.endToEndEncryption && s.hybrid.compressionRatio !== 1.0 && s.hybrid.enabled) s.hybrid.compressionRatio = 1.0;
}
